import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * VirtualTable — общий виртуализированный «бесконечный» список (AK-348).
 *
 * Один компонент вместо постраничной навигации во всех табличных списках
 * (/operations, /fields, platform-admin/companies). Эталон поведения —
 * `docs/superpowers/prototypes/ak-infinite-lists/index.html` (утв. founder
 * 2026-07-03): в DOM только видимое окно ±overscan, порции доезжают сами
 * у нижней кромки, sticky-шапка, живой счётчик, «↑ Наверх» после 2 экранов,
 * зебра + hover, сохранение scroll-позиции при возврате.
 *
 * ⚠️ Новый shared-примитив дизайн-системы — требует Head-Web sign-off.
 *
 * Модель строки — CSS grid: одна `gridTemplate` шарится между шапкой и
 * строками, поэтому колонки всегда выровнены без табличного layout
 * (табличный `<table>` несовместим с абсолютным позиционированием окна).
 *
 * Пагинация — на вызывающей стороне: компонент лишь просит `onLoadMore()`
 * когда пользователь приблизился к концу загруженного и `hasMore === true`.
 * Смена фильтров = смена `resetKey` → скролл в начало + сохранённая позиция
 * этого маршрута сбрасывается.
 */

export interface VirtualColumn<T> {
  /** Стабильный ключ колонки. */
  key: string;
  /** Заголовок колонки. */
  header: React.ReactNode;
  /**
   * CSS grid track (напр. `'118px'`, `'minmax(140px,1fr)'`, `'1fr'`).
   * По умолчанию `minmax(120px, 1fr)`.
   */
  width?: string;
  /** Выравнивание вправо (числовые колонки). */
  align?: 'left' | 'right';
  /** Рендер ячейки. */
  cell: (row: T) => React.ReactNode;
  /** Доп. классы на ячейку. */
  className?: string;
  /**
   * Серверный ключ сортировки для этой колонки (значение `?sort=`).
   * Если задан — заголовок кликабелен и переключает сортировку.
   * Сортировка выполняется НА СЕРВЕРЕ: клик вызывает `onSortChange`, а
   * вызывающая сторона перезапрашивает набор с `sort/order` (не клиентский
   * `.sort()` — иначе на бесконечном списке отсортируется лишь загруженная
   * порция).
   */
  sortKey?: string;
}

/** Текущая серверная сортировка списка. */
export interface SortState {
  /** Ключ колонки (`VirtualColumn.sortKey`). */
  field: string;
  order: 'asc' | 'desc';
}

/**
 * Чистый редуктор клика по сортируемому заголовку (unit-tested).
 *
 * - клик по другой колонке → сортируем по ней, направление `asc`;
 * - клик по активной колонке → инвертируем направление;
 * - третий клик (был `desc`) → сброс к дефолтной сортировке (`null`).
 */
export function nextSortState(
  current: SortState | null,
  field: string,
): SortState | null {
  if (!current || current.field !== field) {
    return { field, order: 'asc' };
  }
  if (current.order === 'asc') return { field, order: 'desc' };
  return null;
}

export interface VirtualTableProps<T> {
  columns: VirtualColumn<T>[];
  rows: T[];
  /** Стабильный ключ строки. */
  rowKey: (row: T) => string;
  /** Полное число записей на сервере (для счётчика «из M»). */
  total?: number;
  /** Есть ли ещё непогруженные порции. */
  hasMore?: boolean;
  /** Идёт ли догрузка следующей порции. */
  loadingMore?: boolean;
  /** Первичная загрузка (список ещё пуст). */
  loading?: boolean;
  /** Текст ошибки (первичная загрузка или догрузка). */
  error?: string | null;
  /** Запросить следующую порцию. Вызывается у нижней кромки. */
  onLoadMore?: () => void;
  /** Повтор после ошибки. */
  onRetry?: () => void;
  /** Клик по строке (напр. навигация в карточку). */
  onRowClick?: (row: T) => void;
  /** Высота строки в px (по умолчанию 48). */
  rowHeight?: number;
  /** Overscan — сколько строк рисуем за пределами вьюпорта (по умолч. 10). */
  overscan?: number;
  /** Высота области прокрутки. По умолчанию `600px`. */
  height?: number | string;
  /** Заголовок счётчика единиц (мн. ч.), напр. «операций». */
  unitLabel?: string;
  /** Текст пустого состояния. */
  emptyText?: string;
  /**
   * Ключ раскрытой строки (`rowKey`) — под ней рисуется `renderExpanded`.
   * Раскрыта всегда не больше одной строки: высота панели измеряется
   * ResizeObserver'ом и подмешивается в размер именно этой строки, поэтому
   * держать несколько одновременно нельзя (AK-470).
   */
  expandedRowKey?: string | null;
  /** Содержимое под раскрытой строкой. Без `expandedRowKey` не вызывается. */
  renderExpanded?: (row: T) => React.ReactNode;
  /**
   * Ключ для сохранения/восстановления scroll-позиции в sessionStorage.
   * Обычно = маршрут (`'operations'`). Без него позиция не сохраняется.
   */
  restoreKey?: string;
  /**
   * Сигнатура текущего набора фильтров. При её изменении список
   * скроллится в начало и сохранённая позиция сбрасывается.
   */
  resetKey?: string;
  /**
   * Текущая серверная сортировка (управляемая вызывающей стороной).
   * `null` = сортировка по умолчанию (сервер решает сам).
   */
  sort?: SortState | null;
  /**
   * Колбэк смены сортировки при клике по сортируемому заголовку.
   * Вызывающая сторона обязана перезапросить набор с сервера с новым
   * `sort/order` и включить `sort`-сигнатуру в `resetKey`, чтобы список
   * сбросился на topmost.
   */
  onSortChange?: (next: SortState | null) => void;
  className?: string;
}

const SS_PREFIX = 'vtbl:scroll:';

function fmtNum(n: number): string {
  return n.toLocaleString('ru-RU');
}

// ---- Pure helpers (unit-tested; keep in sync with render logic) ----

/** Ключ sessionStorage для scroll-позиции маршрута. `null` = не сохраняем. */
export function scrollStorageKey(restoreKey?: string): string | null {
  return restoreKey ? SS_PREFIX + restoreKey : null;
}

/**
 * Оконная математика: индексы первой/последней видимой строки при заданной
 * позиции. Зеркалит поведение виртуализатора для проверки в тестах.
 * `[first, last]` включительно; пустой список → `[0, -1]`.
 */
export function visibleWindow(params: {
  scrollTop: number;
  viewportHeight: number;
  rowHeight: number;
  rowCount: number;
  overscan: number;
}): [number, number] {
  const { scrollTop, viewportHeight, rowHeight, rowCount, overscan } = params;
  if (rowCount <= 0) return [0, -1];
  const first = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
  const last = Math.min(
    rowCount - 1,
    Math.ceil((scrollTop + viewportHeight) / rowHeight) + overscan,
  );
  return [first, last];
}

/**
 * Триггер догрузки: близко ли низ вьюпорта к нижней кромке загруженного
 * (в пределах `rowHeight * 8`), при наличии ещё непогруженных строк и когда
 * ничего не грузится прямо сейчас.
 */
export function shouldLoadMore(params: {
  scrollTop: number;
  viewportHeight: number;
  rowHeight: number;
  loadedCount: number;
  total: number;
  loading: boolean;
  loadingMore: boolean;
}): boolean {
  const {
    scrollTop,
    viewportHeight,
    rowHeight,
    loadedCount,
    total,
    loading,
    loadingMore,
  } = params;
  if (loading || loadingMore) return false;
  if (loadedCount >= total) return false;
  const distanceToBottom = loadedCount * rowHeight - (scrollTop + viewportHeight);
  return distanceToBottom < rowHeight * 8;
}

export function VirtualTable<T>({
  columns,
  rows,
  rowKey,
  total,
  hasMore = false,
  loadingMore = false,
  loading = false,
  error = null,
  onLoadMore,
  onRetry,
  onRowClick,
  rowHeight = 48,
  overscan = 10,
  height = 600,
  unitLabel,
  emptyText = 'Ничего не найдено',
  expandedRowKey = null,
  renderExpanded,
  restoreKey,
  resetKey,
  sort = null,
  onSortChange,
  className,
}: VirtualTableProps<T>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [showToTop, setShowToTop] = useState(false);

  const gridTemplate = useMemo(
    () =>
      columns
        .map((c) => c.width ?? 'minmax(120px, 1fr)')
        .join(' '),
    [columns],
  );

  // Высота раскрытой панели. Измеряем, а не задаём константой: список
  // пользователей компании короткий у одних тенантов и длинный у других,
  // фиксированная высота дала бы либо обрез, либо пустоту (AK-470).
  const [expandedHeight, setExpandedHeight] = useState(0);
  const expandedObserverRef = useRef<ResizeObserver | null>(null);

  const measureExpanded = useCallback((el: HTMLDivElement | null) => {
    expandedObserverRef.current?.disconnect();
    expandedObserverRef.current = null;
    if (!el) {
      setExpandedHeight(0);
      return;
    }
    setExpandedHeight(el.getBoundingClientRect().height);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => {
      setExpandedHeight(entry.target.getBoundingClientRect().height);
    });
    ro.observe(el);
    expandedObserverRef.current = ro;
  }, []);

  useEffect(() => () => expandedObserverRef.current?.disconnect(), []);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) =>
      rowHeight +
      (expandedRowKey && rowKey(rows[index]) === expandedRowKey
        ? expandedHeight
        : 0),
    overscan,
    getItemKey: (index) => rowKey(rows[index]),
  });

  // Пересчёт после смены раскрытой строки / её высоты: без measure()
  // виртуализатор оставит старые offsets и панель наедет на соседей.
  useEffect(() => {
    virtualizer.measure();
  }, [virtualizer, expandedRowKey, expandedHeight]);

  // --- scroll position: reset on filter change, restore on mount/return ---
  const ssKey = scrollStorageKey(restoreKey);
  const prevResetKey = useRef(resetKey);
  const restoredRef = useRef(false);

  // Reset scroll to top when the filter signature changes.
  useEffect(() => {
    if (prevResetKey.current === resetKey) return;
    prevResetKey.current = resetKey;
    restoredRef.current = true; // don't re-restore an old position after a reset
    if (ssKey) sessionStorage.removeItem(ssKey);
    const el = scrollRef.current;
    if (el) el.scrollTop = 0;
  }, [resetKey, ssKey]);

  // Restore saved position once, after first rows have arrived.
  useLayoutEffect(() => {
    if (restoredRef.current || !ssKey || rows.length === 0) return;
    const raw = sessionStorage.getItem(ssKey);
    restoredRef.current = true;
    if (!raw) return;
    const top = Number(raw);
    if (!Number.isFinite(top) || top <= 0) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = top;
  }, [ssKey, rows.length]);

  // Persist position + toggle back-to-top on scroll.
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowToTop(el.scrollTop > el.clientHeight * 2);
    if (ssKey) sessionStorage.setItem(ssKey, String(el.scrollTop));
  }, [ssKey]);

  // --- infinite load: IntersectionObserver on a bottom sentinel ---
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root || !onLoadMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (
          entries[0]?.isIntersecting &&
          hasMore &&
          !loadingMore &&
          !loading
        ) {
          onLoadMore();
        }
      },
      { root, rootMargin: `0px 0px ${rowHeight * 8}px 0px`, threshold: 0 },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [onLoadMore, hasMore, loadingMore, loading, rowHeight, rows.length]);

  const scrollToTop = useCallback(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const items = virtualizer.getVirtualItems();
  const shown = rows.length;
  const showEmpty = !loading && shown === 0 && !error;
  const showInitialError = !!error && shown === 0;

  // ARIA rowcount = header row + all data rows on the server (total ?? loaded).
  // -1 tells assistive tech the total is unknown/still loading.
  const ariaRowCount = (total ?? shown) > 0 ? (total ?? shown) + 1 : -1;

  return (
    <div
      role="table"
      aria-rowcount={ariaRowCount}
      aria-colcount={columns.length}
      className={cn(
        'flex flex-col rounded-md border border-border bg-card overflow-hidden',
        className,
      )}
      style={{ height: typeof height === 'number' ? `${height}px` : height }}
    >
      {/* counter strip */}
      {(total != null || shown > 0) && (
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border bg-muted/40 px-4 py-2 text-xs text-muted-foreground">
          <span>
            Показано <b className="text-foreground tabular-nums">{fmtNum(shown)}</b>
            {total != null && (
              <>
                {' '}из <span className="tabular-nums">{fmtNum(total)}</span>
              </>
            )}
            {unitLabel ? ` ${unitLabel}` : ''}
          </span>
        </div>
      )}

      {/* header (fixed above the scroll region) */}
      <div role="rowgroup" className="shrink-0">
        <div
          role="row"
          aria-rowindex={1}
          className="grid border-b border-border bg-muted/60 backdrop-blur-sm"
          style={{ gridTemplateColumns: gridTemplate }}
        >
          {columns.map((c, ci) => {
            const sortable = !!c.sortKey && !!onSortChange;
            const active = sortable && sort?.field === c.sortKey;
            const ariaSort: React.AriaAttributes['aria-sort'] = active
              ? sort?.order === 'asc'
                ? 'ascending'
                : 'descending'
              : sortable
                ? 'none'
                : undefined;
            const label = (
              <span className="inline-flex items-center gap-1">
                <span className="overflow-hidden text-ellipsis">{c.header}</span>
                {sortable &&
                  (active ? (
                    sort?.order === 'asc' ? (
                      <ArrowUp className="h-3 w-3 shrink-0 text-foreground" aria-hidden />
                    ) : (
                      <ArrowDown className="h-3 w-3 shrink-0 text-foreground" aria-hidden />
                    )
                  ) : (
                    <ArrowUpDown
                      className="h-3 w-3 shrink-0 text-muted-foreground/50"
                      aria-hidden
                    />
                  ))}
              </span>
            );
            return (
              <div
                key={c.key}
                role="columnheader"
                aria-colindex={ci + 1}
                aria-sort={ariaSort}
                className={cn(
                  'px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis',
                  c.align === 'right' && 'text-right',
                )}
              >
                {sortable ? (
                  <button
                    type="button"
                    onClick={() => onSortChange!(nextSortState(sort, c.sortKey!))}
                    className={cn(
                      'inline-flex max-w-full items-center gap-1 rounded uppercase tracking-wide hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      active && 'text-foreground',
                      c.align === 'right' && 'flex-row-reverse',
                    )}
                    aria-label={`Сортировать по «${
                      typeof c.header === 'string' ? c.header : c.key
                    }»`}
                  >
                    {label}
                  </button>
                ) : (
                  label
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* scroll viewport — fills remaining flex space */}
      <div className="relative min-h-0 flex-1">
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="absolute inset-0 overflow-y-auto overflow-x-auto"
          style={{ scrollbarGutter: 'stable' }}
          data-testid="virtual-table-scroller"
        >
          {loading && shown === 0 ? (
            <div className="flex h-full items-center justify-center p-12 text-sm text-muted-foreground">
              Загружаем…
            </div>
          ) : showInitialError ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-12 text-center">
              <div className="text-sm font-medium text-destructive">{error}</div>
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:bg-accent"
                >
                  Повторить
                </button>
              )}
            </div>
          ) : showEmpty ? (
            <div className="flex h-full items-center justify-center p-12 text-sm text-muted-foreground">
              {emptyText}
            </div>
          ) : (
            <div
              role="rowgroup"
              style={{
                height: `${virtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {items.map((vi) => {
                const row = rows[vi.index];
                const zebra = vi.index % 2 === 1;
                const clickable = !!onRowClick;
                const activate = clickable ? () => onRowClick(row) : undefined;
                const isExpanded =
                  !!expandedRowKey && rowKey(row) === expandedRowKey;
                const rowBody = (
                  <div
                    role="row"
                    // ARIA is 1-based and the header occupies row 1, so data
                    // rows start at 2 — lets a screen reader announce «N из M».
                    aria-rowindex={vi.index + 2}
                    onClick={activate}
                    onKeyDown={
                      clickable
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              activate?.();
                            }
                          }
                        : undefined
                    }
                    tabIndex={clickable ? 0 : undefined}
                    aria-label={clickable ? 'Открыть строку' : undefined}
                    className={cn(
                      'grid w-full items-center border-b border-border/70',
                      zebra ? 'bg-muted/20' : 'bg-transparent',
                      isExpanded && 'bg-accent/40',
                      clickable &&
                        'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                      'hover:bg-accent/60',
                    )}
                    style={{
                      gridTemplateColumns: gridTemplate,
                      height: `${rowHeight}px`,
                    }}
                  >
                    {columns.map((c, ci) => (
                      <div
                        key={c.key}
                        role="cell"
                        aria-colindex={ci + 1}
                        className={cn(
                          'overflow-hidden text-ellipsis whitespace-nowrap px-3 text-sm',
                          c.align === 'right' && 'text-right tabular-nums',
                          c.className,
                        )}
                      >
                        {c.cell(row)}
                      </div>
                    ))}
                  </div>
                );

                // Обёртка держит высоту «строка + панель»; сама строка
                // остаётся ровно rowHeight, поэтому раскрытие не растягивает
                // ячейки по вертикали.
                return (
                  <div
                    key={vi.key}
                    role="presentation"
                    className="absolute left-0 top-0 w-full"
                    style={{
                      height: `${vi.size}px`,
                      transform: `translateY(${vi.start}px)`,
                    }}
                  >
                    {rowBody}
                    {isExpanded && renderExpanded && (
                      <div ref={measureExpanded}>{renderExpanded(row)}</div>
                    )}
                  </div>
                );
              })}

              {/* bottom sentinel + loader strip */}
              <div
                ref={sentinelRef}
                role="presentation"
                className="absolute left-0 right-0 flex items-center justify-center"
                style={{
                  top: `${virtualizer.getTotalSize()}px`,
                  height: `${rowHeight}px`,
                }}
              >
                {loadingMore && (
                  <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                    Подгружаем ещё
                    <span className="inline-flex gap-1" aria-hidden>
                      <i className="vtbl-dot h-1.5 w-1.5 rounded-full bg-accent-brand" />
                      <i className="vtbl-dot h-1.5 w-1.5 rounded-full bg-accent-brand [animation-delay:.16s]" />
                      <i className="vtbl-dot h-1.5 w-1.5 rounded-full bg-accent-brand [animation-delay:.32s]" />
                    </span>
                  </span>
                )}
                {!loadingMore && error && shown > 0 && (
                  <span className="inline-flex items-center gap-2 text-xs text-destructive">
                    {error}
                    {onRetry && (
                      <button
                        type="button"
                        onClick={onRetry}
                        className="rounded border border-border px-2 py-0.5 text-foreground hover:bg-accent"
                      >
                        Повторить
                      </button>
                    )}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* back-to-top */}
        <button
          type="button"
          onClick={scrollToTop}
          aria-label="Наверх"
          className={cn(
            'absolute bottom-4 right-4 z-20 inline-flex items-center gap-1.5 rounded-full bg-foreground px-3.5 py-2 text-xs font-semibold text-background shadow-lg transition-all',
            showToTop
              ? 'pointer-events-auto opacity-100 translate-y-0'
              : 'pointer-events-none opacity-0 translate-y-2',
          )}
        >
          <ArrowUp className="h-3.5 w-3.5" />
          Наверх
        </button>
      </div>
    </div>
  );
}

export default VirtualTable;
